import { jsxs, Fragment, jsx } from 'react/jsx-runtime';
import { Navbar, SKIP_LINK_TARGETS, Container, Anchor, MiroLogo } from '@mirohq-internal/website-ui-foundation';
import { UserStatus, useUserStatus } from '@mirohq-internal/website-utils';
import classNames from 'classnames';
import React from 'react';
import { MainMenu } from './components/main-menu.esm.js';
import { MetaMenu } from './components/meta-menu.esm.js';
import { MiroDrawer } from './miro-drawer.esm.js';

/**
 * Miro Header is our main header for the site.
 * It contains the main navigation and the meta menu and a drawer
 * menu for breakpoints below xl.
 *
 * @figma - https://www.figma.com/design/utdKqeub7vsQE8rVa0mB28?node-id=10085-21561&node-type=INSTANCE&m=dev
 */ const MiroHeaderPresenterV2 = ({ metaMenuLinks, skipLinkLabel, menu, userStatus = UserStatus.loggedIn, ariaLabel })=>{
    const [isDrawerOpen, setIsDrawerOpen] = React.useState(false);
    return jsxs(Fragment, {
        children: [
            jsx(Navbar, {
                id: SKIP_LINK_TARGETS.PRIMARY_NAVIGATION,
                sticky: "top",
                "data-skip-link": skipLinkLabel || 'Primary Navigation',
                className: "container pt-200 pt-md-300 justify-content-center position-sticky",
                style: {
                    /**
                     * This z-index is used to counter the z-index that other sticky elements on
                     * the page may have.
                     */ zIndex: '1021',
                    /**
                     * The body line height is set to 1.35 which means the default height of
                     * the navbar is 1.35rem. We need to set the line height to 0 to remove
                     * the extra space.
                     */ lineHeight: 0
                },
                "aria-label": ariaLabel,
                children: jsxs(Container, {
                    className: "rounded-150 shadow-50 mx-175 mx-lg-100 p-150 p-md-200 bg-white",
                    children: [
                        jsxs("div", {
                            className: "d-flex gap-150 gap-xl-300",
                            children: [
                                jsx("span", {
                                    className: "d-flex d-xl-none align-self-center",
                                    children: jsx(Anchor, {
                                        "aria-label": "Open Menu",
                                        className: "fs-3 px-50",
                                        iconStart: "LinesThreeHorizontal",
                                        size: "md",
                                        circularBg: true,
                                        withHoverBg: true,
                                        onClick: ()=>{
                                            setIsDrawerOpen(!isDrawerOpen);
                                        }
                                    })
                                }),
                                jsx("span", {
                                    className: classNames('me-auto flex-shrink-0'),
                                    children: jsxs(Anchor, {
                                        "data-testid": "logo-cta",
                                        title: "Go to homepage",
                                        "aria-label": "Go to homepage",
                                        href: "/",
                                        children: [
                                            jsx(MiroLogo.Dark, {
                                                role: "img",
                                                className: "d-block d-md-none icon-size-400",
                                                width: "87.6px",
                                                height: "32px",
                                                "aria-label": "miro logo"
                                            }),
                                            jsx(MiroLogo.Dark, {
                                                className: "d-none d-md-block",
                                                width: "109.5px",
                                                height: "40px",
                                                "aria-label": "miro logo"
                                            })
                                        ]
                                    })
                                }),
                                jsx(MainMenu, {
                                    menu: menu
                                })
                            ]
                        }),
                        jsx("div", {
                            className: "d-none d-md-flex gap-200 justify-content-around",
                            children: jsx(MetaMenu, {
                                userStatus: userStatus,
                                ...metaMenuLinks
                            })
                        })
                    ]
                })
            }),
            jsx(MiroDrawer, {
                metaMenuLinks: metaMenuLinks,
                menu: menu,
                isOpen: isDrawerOpen,
                setIsOpen: setIsDrawerOpen,
                userStatus: userStatus
            })
        ]
    });
};
const MiroHeaderV2 = (props)=>{
    const userStatus = useUserStatus();
    return jsx(MiroHeaderPresenterV2, {
        ...props,
        userStatus: userStatus
    });
};

export { MiroHeaderPresenterV2, MiroHeaderV2 };
